import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { consoleLogError, consoleLogInfo, consoleLogSuccess } from "../../utils/console";
import {
  CUSTOM_EVENT_TYPE_STASH_INSTALL_PROMPT,
  NAMED_CACHE_NAME,
  STORAGE_KEY_IS_USER_CANCELED_AUTO_DOWNLOADING,
  SW_EVENTS_TYPES_FROM,
  WINDOW_KEY_INSTALL_EVENT
} from "../../utils/sw/sw.constants";
import { getBookIdWithPrefix, getBookIdNumber } from "../../utils/content/content";
import { selectorTocItems } from "../../store/toc/selectors";
import {
  selectorIsWebsiteDownloaded
} from "../../store/sw/selectorIsWebsiteDownloaded";
import { _sharedActionsSW } from "../../store/sw/actions";
import { _sharedActionsSystem } from "../../store/system/actions";
import { getLocalStorageItem } from "../../utils/storage/storage";
import { dispatchCustomEvent } from "../../utils/dom";
import {
  CUSTOM_EVENT_APP_VERSION_DIFFERS,
  CUSTOM_EVENT_CONTENT_VERSION_DIFFERS
} from "../../utils/storage/storage.constants";
import {
  postSWMessageClearNamedCaches,
  postSWMessageClearWebsiteData, registerServiceWorker,
} from "../../utils/sw/sw";
import { isProduction } from "../../utils/assembly";
import { useIsStandAlone } from "./pwa";
import { useSelectorBook } from "../../store/mainTree/hooks-selectors";

import packageJson from "../../../main/package.json";
import { parseZipBookDownload } from "@/shared/utils/zip";
import { selectorIsSWRegistered } from "@/shared/store/sw/selectorIsSWRegistered";
import { selectorIsSWInit } from "@/shared/store/sw/selectorIsSWInit";
import { selectorIsSWInitStatusRetrieved } from "@/shared/store/sw/selectorIsSWInitStatusRetrieved";
import { selectorIsErrorWebsiteDownload } from "@/shared/store/sw/selectorIsErrorWebsiteDownload";
import {
  selectorWebsiteUrlsForOfflineUse
} from "@/shared/store/sw/selectorWebsiteUrlsForOfflineUse";
import { selectorIsDownloadingWebsite } from "@/shared/store/sw/selectorIsDownloadingWebsite";
import { selectorDownloadedBooksIds } from "@/shared/store/sw/selectorDownloadedBooksIds";

const IS_CONSOLE_LOGS = isProduction() || false;

const scope = "SW shared";

export const useRegisterServiceWorker = (scriptURL) => {
  const dispatch = useDispatch();

  useEffect(() => {
    registerServiceWorker(scriptURL + "?v=" + packageJson.version, (registration) => {
      dispatch(_sharedActionsSW.updateIsSWRegistered(true));

      if (IS_CONSOLE_LOGS) {
        consoleLogSuccess(scope, "Registration successful! Scope: ", registration.scope);
      }
    }, (err) => {
      consoleLogError(scope, "Registration failed. Error: ", err);
    }, () => {
      dispatch(_sharedActionsSW.updateIsSWInit(true));
    });
  }, [scriptURL]);
};

const createSWMessageListener = (dispatch) => async (event) => {
  try {
    if (IS_CONSOLE_LOGS) {
      consoleLogInfo(scope, "Message event: ", event.data);
    }

    switch (event.data.type) {
      case SW_EVENTS_TYPES_FROM.UPDATE_NAMED_CACHE_KEYS_AVAILABLE: {
        dispatch(_sharedActionsSW.updateKeysAvailable(
          event.data.data.cacheName, event.data.data.keys
        ));
        break;
      }
      case SW_EVENTS_TYPES_FROM.GET_FROM_NAMED_CACHE_SUCCESS: {
        let { value } = event.data.data;

        const { cacheName } = event.data.data;

        if (cacheName === NAMED_CACHE_NAME.BOOK_DATA) {
          value = await parseZipBookDownload(value);
        }

        dispatch(_sharedActionsSW.pullSuccess(
          event.data.data.cacheName, event.data.data.key, value
        ));
        break;
      }

      case SW_EVENTS_TYPES_FROM.START_DOWNLOAD_URLS:
      case SW_EVENTS_TYPES_FROM.CANCEL_DOWNLOAD_URLS:
      case SW_EVENTS_TYPES_FROM.ERROR_DOWNLOAD_URLS:
      case SW_EVENTS_TYPES_FROM.END_DOWNLOAD_URLS: {
        if (event.data.type !== SW_EVENTS_TYPES_FROM.ERROR_DOWNLOAD_URLS) {
          dispatch(_sharedActionsSW.updateIsWebsiteUrlsDownloading(
            event.data.type === SW_EVENTS_TYPES_FROM.START_DOWNLOAD_URLS
          ));
        }

        if (event.data.type === SW_EVENTS_TYPES_FROM.END_DOWNLOAD_URLS
          || event.data.type === SW_EVENTS_TYPES_FROM.CANCEL_DOWNLOAD_URLS) {
          dispatch(_sharedActionsSW.updateWebsiteUrlsStatuses(event.data.data));
        }

        if (event.data.type === SW_EVENTS_TYPES_FROM.ERROR_DOWNLOAD_URLS) {
          dispatch(_sharedActionsSW.updateIsErrorWebsiteDownload(true));
        }

        if (event.data.type === SW_EVENTS_TYPES_FROM.CANCEL_DOWNLOAD_URLS) {
          dispatch(_sharedActionsSystem.toast("@sw:downloadCancelled"));
        }
        break;
      }
      case SW_EVENTS_TYPES_FROM.DOWNLOAD_URLS_PROGRESS: {
        dispatch(_sharedActionsSW.updateWebsiteUrlsProgress(event.data.data));
        break;
      }

      case SW_EVENTS_TYPES_FROM.GET_INIT_DATA_SUCCESS: {
        const namedCachesNames = Object.keys(event.data.data.namedCaches);
        namedCachesNames.forEach((cacheKey) => {
          dispatch(_sharedActionsSW.updateKeysAvailable(
            cacheKey,
            event.data.data.namedCaches[cacheKey]
          ));
        });
        dispatch(_sharedActionsSW.updateWebsiteUrlsStatuses(event.data.data.urlsStatuses));
        dispatch(_sharedActionsSW.updateIsSWInitDataRetrieved(true));
        break;
      }
    }
  } catch (e) {
    consoleLogError(scope, "Error parsing event data: ", e);
  }
};

export const useHandleSWMessages = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const listener = createSWMessageListener(dispatch);

    global.window?.navigator?.serviceWorker?.addEventListener("message", listener);

    return () => {
      global.window?.navigator?.serviceWorker?.removeEventListener("message", listener);
    };
  }, []);
};

const tocEmpty = [];
export const useBookWithSWFallback = (id) => {
  const dispatch = useDispatch();

  const strBookId = id ? getBookIdNumber(getBookIdWithPrefix(id)).toString() : "";
  const bookRedux = useSelectorBook(strBookId);
  const tocItems = useSelector(selectorTocItems);
  const downloadedBooksIds = useSelector(selectorDownloadedBooksIds);

  const toc = bookRedux && tocItems[bookRedux.book_id] || tocEmpty;

  // Pulls a book from SW.
  useEffect(() => {
    if (!bookRedux && strBookId && downloadedBooksIds.includes(strBookId)) {
      dispatch(_sharedActionsSW.pullBookData(strBookId));
    }
  }, [bookRedux, strBookId, downloadedBooksIds]);

  return { book: bookRedux, toc };
};

export const useStashInstallPWAPrompt = () => {
  // Stash install PWA prompt to a global variable.
  useEffect(() => {
    const listener = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      global.window[WINDOW_KEY_INSTALL_EVENT] = e;
      dispatchCustomEvent(CUSTOM_EVENT_TYPE_STASH_INSTALL_PROMPT);
    };

    window.addEventListener("beforeinstallprompt", listener);

    return () => {
      window.removeEventListener("beforeinstallprompt", listener);
    };
  }, []);
};

export const useAutoDownloadWebsite = (enabled = true) => {
  const dispatch = useDispatch();

  const isSWInit = useSelector(selectorIsSWInit);
  const isSWRegistered = useSelector(selectorIsSWRegistered);
  const isSWInitStatusRetrieved = useSelector(selectorIsSWInitStatusRetrieved);
  const urlsForOfflineUse = useSelector(selectorWebsiteUrlsForOfflineUse);
  const isWebsiteDownloaded = useSelector(selectorIsWebsiteDownloaded);
  const isErrorWebsiteDownload = useSelector(selectorIsErrorWebsiteDownload);
  const isUrlsDownloading = useSelector(selectorIsDownloadingWebsite);
  const isStandAlone = useIsStandAlone();

  // Asks SW to return status data.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (isSWInit && isSWRegistered && urlsForOfflineUse.length) {
      dispatch(_sharedActionsSW.getInitStatus());
    }
  }, [enabled, isSWInit, isSWRegistered, urlsForOfflineUse]);

  // Downloads website.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const isUserCanceledDownloadAtLeastOnce = getLocalStorageItem(
      STORAGE_KEY_IS_USER_CANCELED_AUTO_DOWNLOADING
    );
    if (
      isStandAlone
      && isSWInitStatusRetrieved
      && !isErrorWebsiteDownload
      && !isWebsiteDownloaded
      && !isUrlsDownloading
      && !isUserCanceledDownloadAtLeastOnce
    ) {
      dispatch(_sharedActionsSW.downloadWebsite());
    }
  }, [
    isStandAlone,
    isUrlsDownloading, isErrorWebsiteDownload, isWebsiteDownloaded, isSWInitStatusRetrieved,
    urlsForOfflineUse,
    enabled,
  ]);
};

export const useClearSWDataWhenVersionsDiffers = () => {
  // Removes cache urls if the app version became different.
  useEffect(() => {
    const listener = () => {
      postSWMessageClearWebsiteData();
    };
    window.addEventListener(CUSTOM_EVENT_APP_VERSION_DIFFERS, listener);

    return () => {
      window.removeEventListener(CUSTOM_EVENT_APP_VERSION_DIFFERS, listener);
    };
  }, []);  // Removes cache urls if the app version became different.

  // Removes named cache(s) contents if the content version became different.
  useEffect(() => {
    const listener = () => {
      postSWMessageClearNamedCaches();
    };
    window.addEventListener(CUSTOM_EVENT_CONTENT_VERSION_DIFFERS, listener);

    return () => {
      window.removeEventListener(CUSTOM_EVENT_CONTENT_VERSION_DIFFERS, listener);
    };
  }, []);
};
